import { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { useSwipe } from "@/hooks/useSwipe";

// Customer Stories — one immersive editorial moment (merges the former
// "Skincare, felt. Not sold." canvas with the story rotator).
//
// AUTHENTICITY NOTE: Riversol's reviews page does not expose a verified
// pairing between a specific customer face-photo and a specific named
// review, so we do NOT attach customer faces to names. Every story is
// built only from the atomic, verified unit that appears together in one
// official review: {name + verbatim quote + product + 5★}. The changing
// media is the official product image the review is actually about — the
// one relationship the site explicitly connects. No invented content and
// no unverified review totals.
const STORIES = [
  {
    name: "Maryanne",
    product: "Peptide Hand Cream",
    rating: 5,
    quote:
      "The peptide hand cream is not greasy and is fast absorbing. My hands are so soft and the age spots are less noticeable and a definite reduction in wrinkles. I saw the biggest difference in my nails. They have stopped splitting and look fabulous. I have to say I was sceptical, having used so many hand creams with no results. But Riversol has come to the rescue with a winning product. Thank you!",
    image: "https://www.riversol.com/cdn/shop/files/Peptide_Hand_Cream.png?v=1786982562&width=1000",
  },
  {
    name: "Brenda U.",
    product: "Anti-Aging Trio Bundle",
    rating: 5,
    quote:
      "The trio is great skin care routine. This is the first product I've tried in many years that hasn't triggered my eczema. I've been using the Riversol trio for about a year and I love the way my skin feels.",
    image: "https://www.riversol.com/cdn/shop/products/AA-Normal.png?v=1776103480&width=1200",
    fadeStrong: true,
  },
  {
    name: "Marlene B.",
    product: "Daily Glow Mineral Sunscreen SPF 50+",
    rating: 5,
    quote:
      "Love the consistency, shade and coverage of the Daily Glow Mineral Sunscreen it's my new favourite facial sunscreen! and at 50+ SPF I know my delicate facial skin is well covered!",
    image:
      "https://www.riversol.com/cdn/shop/files/Daily-Glow-Mineral-Sunscreen-over-texture-and-aplicator_UNTINTED.png?v=1776126826&width=1000",
  },
  {
    name: "Denise C.",
    product: "Hydrating Cream Cleanser",
    rating: 5,
    quote: "I love the look and feel of my face after I use this cleanser.",
    image: "https://www.riversol.com/cdn/shop/products/White_BG_-_Cream_Cleanser.png?v=1776098620&width=1000",
  },
  {
    name: "Lpierce",
    product: "Refreshing Gel Cleanser",
    rating: 5,
    quote: "Love it. My face feels great",
    image: "https://www.riversol.com/cdn/shop/files/NewGelCleanserV2.png?v=1776098691&width=1000",
  },
  {
    name: "Marlene R.",
    product: "Anti-Aging Serum (30mL)",
    rating: 5,
    quote: "love this serum. I use it in am after shower. My skin feels hydrated and smooth.",
    image: "https://www.riversol.com/cdn/shop/products/aa-serum.png?v=1776095507&width=1000",
  },
  {
    name: "JLS",
    product: "Daily Moisturizing Cream",
    rating: 5,
    quote:
      "As a previous patient of Dr. Rivers I was confident his products would be safe and effective. I have been using them since the inception of the line.",
    image: "https://www.riversol.com/cdn/shop/products/dmc_8215c1a3-320f-40e2-8cda-af33d45a4928.png?v=1776098827&width=1000",
  },
];

const AUTOPLAY_MS = 8000;

const Stars = ({ n }) => (
  <div className="flex items-center gap-1" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: n }).map((_, k) => (
      <Star key={k} size={15} strokeWidth={0} className="fill-white text-white" />
    ))}
  </div>
);

export const CustomerStories = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setI((v) => (v + 1) % STORIES.length), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused]);

  const go = useCallback(
    (n) => setI(((n % STORIES.length) + STORIES.length) % STORIES.length),
    []
  );
  const slide = STORIES[i];
  const swipe = useSwipe(() => go(i + 1), () => go(i - 1));

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        height: "88svh",
        minHeight: 600,
        maxHeight: 900,
        background:
          "radial-gradient(120% 90% at 78% 30%, #143029 0%, #0c1d19 45%, #06110e 100%)",
      }}
      data-testid="home-customer-stories"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      {...swipe}
    >
      {/* subtle grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* CHANGING MEDIA — official product image for the active review */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-[52%] pointer-events-none">
        <AnimatePresence>
          <motion.div
            key={`media-${i}`}
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src={slide.image}
              alt={slide.product}
              className="max-h-[46vh] lg:max-h-[58vh] w-auto object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.45)]"
              style={{
                WebkitMaskImage: slide.fadeStrong
                  ? "linear-gradient(to bottom, #000 58%, rgba(0,0,0,0.2) 80%, transparent 93%)"
                  : "linear-gradient(to bottom, #000 86%, transparent 99%)",
                maskImage: slide.fadeStrong
                  ? "linear-gradient(to bottom, #000 58%, rgba(0,0,0,0.2) 80%, transparent 93%)"
                  : "linear-gradient(to bottom, #000 86%, transparent 99%)",
              }}
              draggable={false}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      {/* left readability wash over the media on small screens */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#06110e] via-[#06110e]/70 lg:via-[#06110e]/30 to-transparent pointer-events-none" />

      {/* TOP meta rail */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="absolute left-5 sm:left-6 lg:left-12 top-6 lg:top-10 z-10 flex items-center gap-4"
      >
        <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/55">
          {String(i + 1).padStart(2, "0")} / {String(STORIES.length).padStart(2, "0")}
        </span>
        <span className="w-8 h-px bg-white/25" />
        <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/55">
          Customer Stories
        </span>
      </motion.div>

      {/* CONTENT — framing headline (reduced) + rotating verified story */}
      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-5 sm:px-6 lg:px-12 xl:px-16 flex flex-col justify-center">
        <div className="max-w-[560px]">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-white text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.1] tracking-[-0.01em]"
          >
            Skincare, felt. <span className="italic text-white/55">Not sold.</span>
          </motion.h2>

          <div className="mt-7 lg:mt-8 min-h-[280px] sm:min-h-[260px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={`story-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              >
                <Stars n={slide.rating} />
                <blockquote
                  className="mt-4 text-white text-[19px] sm:text-[21px] lg:text-[24px] leading-[1.42] font-light tracking-[-0.005em]"
                  data-testid="story-quote"
                >
                  “{slide.quote}”
                </blockquote>
                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[11px] tracking-[0.26em] uppercase text-white">
                    {slide.name}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/40" />
                  <span className="text-[13px] text-white/70">{slide.product}</span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* mobile selector */}
          <div className="mt-8 flex items-center gap-2 lg:hidden">
            {STORIES.map((s, idx) => (
              <button
                key={s.name}
                type="button"
                onClick={() => go(idx)}
                aria-label={`Story about ${s.product}`}
                data-testid={`story-dot-${idx}`}
                className="py-2"
              >
                <span
                  className={`block h-[2px] transition-all duration-500 ${
                    idx === i ? "w-8 bg-white" : "w-4 bg-white/40"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* THUMB RAIL — right, official product images (verified to each review) */}
      <div className="absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 z-10 hidden lg:flex flex-col gap-2.5">
        {STORIES.map((s, idx) => (
          <motion.button
            key={s.name}
            type="button"
            onClick={() => go(idx)}
            aria-label={`Story about ${s.product}`}
            data-testid={`story-thumb-${idx}`}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: idx === i ? 1 : 0.5, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 + idx * 0.06 }}
            className={`group relative w-[60px] h-[60px] rounded-full overflow-hidden bg-white/5 backdrop-blur-sm transition-all duration-500 ${
              idx === i
                ? "ring-1 ring-white/80 opacity-100 scale-100"
                : "opacity-50 hover:opacity-90 scale-[0.92]"
            }`}
          >
            <img
              src={s.image}
              alt=""
              className="w-full h-full object-contain p-1.5"
              loading="lazy"
              draggable={false}
            />
          </motion.button>
        ))}
      </div>

      {/* See more stories */}
      <a
        href="https://www.riversol.com/pages/reviews"
        data-testid="stories-read-all"
        className="absolute left-5 sm:left-6 lg:left-12 xl:left-16 bottom-7 lg:bottom-10 z-10 group inline-flex items-center gap-2 text-white text-[12px] lg:text-[13px] tracking-[0.14em] uppercase font-medium border-b border-white/40 pb-2 hover:border-white transition-colors"
      >
        See more stories
        <ArrowRight size={15} strokeWidth={1.8} className="transition-transform group-hover:translate-x-0.5" />
      </a>

      {/* progress rail */}
      <div className="absolute inset-x-0 bottom-0 h-[2px] bg-white/10 z-10">
        <motion.div
          key={`bar-${i}-${paused}`}
          initial={{ width: "0%" }}
          animate={{ width: paused ? "0%" : "100%" }}
          transition={{ duration: paused ? 0 : AUTOPLAY_MS / 1000, ease: "linear" }}
          className="h-full bg-white/80"
        />
      </div>
    </section>
  );
};

export default CustomerStories;
